/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types'
import React from 'react'

import ButtonType from './button-type'
import LinkType from './link-type'
import {useCasing} from '../../hooks/use-title-case'

const Link = ({
    url,
    externalUrl,
    type = 'primary',
    useNavLink = false,
    isExternal = false,
    openLinkInNewTab = false,
    text,
    iconName,
    iconStyles = {},
    iconComponent,
    buttonStyles,
    onLinkClick,
    children,
    inactive = false,
    buttonType = 'button',
    isTitleCase = true,
    ...props
}) => {
    const {updatedText} = useCasing({isTitleCase, text})
    return (
        <>
            {url || externalUrl ? (
                <LinkType
                    href={url}
                    externalUrl={externalUrl}
                    type={type}
                    useNavLink={useNavLink}
                    isExternal={isExternal}
                    openLinkInNewTab={openLinkInNewTab}
                    text={updatedText}
                    icon={iconName}
                    iconStyles={iconStyles}
                    iconComponent={iconComponent}
                    buttonStyles={buttonStyles}
                    {...props}
                >
                    {children}
                </LinkType>
            ) : (
                <ButtonType
                    onLinkClick={onLinkClick}
                    type={type}
                    text={updatedText}
                    icon={iconName}
                    iconComponent={iconComponent}
                    buttonStyles={buttonStyles}
                    inactive={inactive}
                    iconStyles={iconStyles}
                    buttonType={buttonType}
                    {...props}
                >
                    {children}
                </ButtonType>
            )}
        </>
    )
}

Link.displayName = 'Link'

Link.propTypes = {
    url: PropTypes.string,
    externalUrl: PropTypes.string,
    useNavLink: PropTypes.bool,
    /**
     * isExternal means it is external link/complete link which starts with https://
     */
    isExternal: PropTypes.bool,
    openLinkInNewTab: PropTypes.bool,
    text: PropTypes.string,
    buttonStyles: PropTypes.object,
    onLinkClick: PropTypes.func,
    iconName: PropTypes.string,
    iconStyles: PropTypes.object,
    iconComponent: PropTypes.object,
    children: PropTypes.node,
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'unstyled']),
    inactive: PropTypes.bool,
    buttonType: PropTypes.oneOf(['button', 'submit', 'reset']),
    isTitleCase: PropTypes.bool
}

export default React.memo(Link)
